import parse, { domToReact } from 'html-react-parser'
import React, { useState } from 'react'
import OpsotButton from '../components/ui/OpsotButton'
import encryptedStorage from './encryptedStorage'

let iframeCounter = 0
export const handleImageError = (index, setImageErrors) => {
	setImageErrors((prevErrors) => {
		const newErrors = [...prevErrors]
		newErrors[index] = true
		return newErrors
	})
}

export const isExternalUrl = (url) => {
	try {
		const link = new URL(url, window.location.origin)
		return link.origin !== window.location.origin
	} catch (error) {
		return false
	}
}

export const scrollToTop = (offset = 0, behavior = 'smooth') => {
	window.scrollTo({
		top: offset,
		behavior
	})
}

export const sanitiseHtml = (html) => html.replace(/&nbsp;/g, ' ')

export const handleIframes = (htmlString) =>
	parse(htmlString, {
		replace: (domNode) => {
			if (domNode.name === 'iframe' && domNode.attribs) {
				const { src, 'data-src': dataSrc, title } = domNode.attribs

				if (!src && dataSrc) {
					domNode.attribs.src = dataSrc
				}

				return (
					<iframe
						{...domNode.attribs}
						title={title || `iframe-${++iframeCounter}`}
					/>
				)
			}

			if (
				domNode.name === 'div' &&
				domNode.attribs &&
				domNode.attribs.class?.includes('transcript')
			) {
				const transcriptContent = domNode.children
					? domToReact(domNode.children)
					: null

				return <TranscriptAccordion content={transcriptContent} />
			}

			return undefined
		}
	})

export const removeMediaErrors = (html) => {
	const parser = new DOMParser()
	const doc = parser.parseFromString(html, 'text/html')

	// Remove all elements with the class 'media-embed-error'
	doc.querySelectorAll('.media-embed-error').forEach((el) => el.remove())

	return doc.body.innerHTML
}

const TranscriptAccordion = ({ content }) => {
	const [isOpen, setIsOpen] = useState(false)

	return (
		<div className="transcript-container">
			<OpsotButton
				small
				sx={{ marginBottom: '1rem' }}
				onClick={() => setIsOpen((prev) => !prev)}
				className="transcript-toggle"
				text={isOpen ? 'Click to hide transcript' : 'Click to view transcript'}
			/>
			{isOpen && <div className="transcript-content">{content}</div>}
		</div>
	)
}

export const copyFullUrlToClipboard = () => {
	const fullUrl = window.location.href

	if (navigator.clipboard && navigator.clipboard.writeText) {
		navigator.clipboard
			.writeText(fullUrl)
			.then(() => {
				console.log('URL copied to clipboard:', fullUrl)
			})
			.catch((error) => {
				console.error('Failed to copy URL using Clipboard API:', error)
			})
	} else {
		// Fallback to deprecated execCommand method
		try {
			const tempInput = document.createElement('input')
			document.body.appendChild(tempInput)
			tempInput.value = fullUrl
			tempInput.select()
			document.execCommand('copy')
			document.body.removeChild(tempInput)
			console.log('URL copied to clipboard (fallback):', fullUrl)
		} catch (error) {
			console.error('Failed to copy URL using fallback method:', error)
		}
	}
}

export const isSocialMediaLink = (url) => {
	const socialMediaDomains = [
		'facebook.com',
		'twitter.com',
		'instagram.com',
		'linkedin.com',
		'x.com'
	]
	return socialMediaDomains.some((domain) => url.includes(domain))
}

export const setLocalStorage = (key, value) => {
	if (typeof window !== 'undefined') {
		encryptedStorage.setItem(key, value)
	}
}

export const getLocalStorage = (key) => {
	if (typeof window !== 'undefined') {
		return encryptedStorage.getItem(key)
	}
	return null
}

export const removeLocalStorage = (key) => {
	if (typeof window !== 'undefined') {
		encryptedStorage.removeItem(key)
	}
}

export const getUserAndOrg = () => {
	const eventData = getLocalStorage('login_success_event_data')
	const userHash = eventData?.userHash || null
	const organisation = eventData?.organisation || null
	return {
		userHash,
		organisation
	}
}

export const setUserAndOrg = (userHash, organisation) => {
	setLocalStorage('login_success_event_data', { userHash, organisation })
}

export const removeUserAndOrg = () => {
	removeLocalStorage('login_success_event_data')
}

export const scrollToToolkitSection = () => {
	const urlParams = new URLSearchParams(window.location.search)
	const sectionId = urlParams.get('toolkit_section_nid')
	let intervalCount = 0

	if (sectionId) {
		const getElementInterval = setInterval(() => {
			intervalCount++
			const targetElement = document.getElementById(sectionId)

			if (targetElement) {
				clearInterval(getElementInterval)
				setTimeout(() => {
					targetElement.click()
				}, 500)
			}
			if (intervalCount > 500) {
				clearInterval(getElementInterval)
			}
		}, 100)
	}
}

export const limitTextToSentences = (text, maxSentences = 2) => {
	const sentences = text.match(/[^.!?]+[.!?]+/g) || [text]
	return sentences.slice(0, maxSentences).join(' ').trim()
}

export const createFloatingBox = ({
	text,
	textArray,
	theme,
	headerSelector = '.mui-fixed',
	zIndex
}) => {
	let floatingBox = document.querySelector('#floating-box')

	if (!floatingBox) {
		floatingBox = document.createElement('div')
		floatingBox.id = 'floating-box' // Prevent multiple boxes
		floatingBox.style.position = 'fixed'
		floatingBox.style.left = '0'
		floatingBox.style.width = '100%'
		floatingBox.style.backgroundColor = theme.palette.primary.tint3
		floatingBox.style.border = `1px solid ${theme.palette.primary.main}`
		floatingBox.style.color = '#000000'
		floatingBox.style.padding = '10px'
		floatingBox.style.textAlign = 'center'
		floatingBox.style.display = 'none'
		floatingBox.style.zIndex = zIndex
		document.body.appendChild(floatingBox)
	}
	const updateTopPosition = () => {
		const header = document.querySelector(headerSelector)
		floatingBox.style.top = header ? `${header.offsetHeight}px` : '0px'
	}
	updateTopPosition()

	if (!floatingBox.dataset.listenerAdded) {
		floatingBox.dataset.listenerAdded = 'true'
		window.addEventListener('resize', updateTopPosition)

		floatingBox.removeEventListener = () => {
			window.removeEventListener('resize', updateTopPosition)
		}
	}

	floatingBox.innerText =
		textArray?.length > 0 ? textArray[textArray.length - 1] : text
	return floatingBox
}

export const formatTimestamp = (timestamp) =>
	new Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: 'long',
		year: 'numeric'
	}).format(new Date(timestamp * 1000))

export const extractParentToolkitNids = (resultsArray) =>
	resultsArray.flatMap((result) =>
		result.grouped_hits.flatMap((group) =>
			group.hits.map((hit) => hit.document.parent_toolkit_nid)
		)
	)

const MAX_TITLE_LENGTH = 58
export const getMostRecentlyUpdatedToolkit = (data) => {
	let latestToolkit = null

	if (data) {
		Object.values(data).forEach((category) => {
			Object.values(category).forEach((subcategory) => {
				if (subcategory?.toolkits) {
					Object.values(subcategory.toolkits).forEach((toolkit) => {
						if (
							!latestToolkit ||
							toolkit.updatedTimestamp > latestToolkit.updatedTimestamp
						) {
							latestToolkit = toolkit
						}
					})
				}
			})
		})
	}

	if (!latestToolkit) {
		return {
			nid: '',
			title: '',
			published: false,
			updated: false
		}
	}

	const isJustPublished =
		latestToolkit.publishedTimestamp === latestToolkit.updatedTimestamp

	let truncatedTitle = latestToolkit.title
	if (truncatedTitle.length > MAX_TITLE_LENGTH) {
		truncatedTitle = `${truncatedTitle.slice(0, MAX_TITLE_LENGTH - 3)}...`
	}

	return {
		...latestToolkit,
		title: truncatedTitle,
		published: isJustPublished,
		updated: !isJustPublished
	}
}

const isKnownFileLink = (url, knownHosts, knownPaths) => {
	try {
		const parsed = new URL(url)
		const isKnownHost = knownHosts.includes(parsed.hostname)
		const isKnownPath = knownPaths.some((path) =>
			parsed.pathname.startsWith(path)
		)

		return isKnownHost && isKnownPath
	} catch {
		return false
	}
}

export const isPrivateFileLink = (url) => {
	const knownHosts = [
		'www.college.police.uk',
		'staging.copweb.aws.college.police.uk',
		'dev.copweb.aws.college.police.uk',
		'cop.ddev.site'
	]
	const knownPaths = ['/system/files/']

	return isKnownFileLink(url, knownHosts, knownPaths)
}

export const isPublicFileLink = (url) => {
	const knownHosts = [
		'www.college.police.uk',
		'staging.copweb.aws.college.police.uk',
		'dev.copweb.aws.college.police.uk',
		'cop.ddev.site',
		'assets.college.police.uk',
		'assets.staging.copweb.aws.college.police.uk',
		'assets.dev.copweb.aws.college.police.uk'
	]
	const knownPaths = [
		'/s3fs-public/',
		'/cop/s3fs-public/',
		'/s3fs_to_s3/files/'
	]

	return isKnownFileLink(url, knownHosts, knownPaths)
}

export const saveFileFromUrl = async (url, userToken) => {
	try {
		const response = await fetch(url, {
			method: 'GET',
			headers: userToken ? { 'X-ACCESS-AUTH-TOKEN': userToken } : {}
		})

		if (!response.ok) {
			throw new Error(`The server responded with status ${response.status}.`)
		}

		if (!response.headers.get('Last-Modified')) {
			throw new Error(
				`The response does not include a 'Last-Modified' header which indicates it is not a file.`
			)
		}

		const blob = await response.blob()
		let filename = new URL(url).pathname.split('/').pop() || 'file'
		filename = decodeURIComponent(filename)

		const objectUrl = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = objectUrl
		link.download = filename

		document.body.appendChild(link)
		link.click()
		link.remove()
		URL.revokeObjectURL(objectUrl)
	} catch (error) {
		console.error(`Error saving file from URL ${url}.`, error)
	}
}
